import { useState } from 'react';
import { photoSrc, sortedPhotoKeys } from 'modules/gallery';
import { createAlbum } from 'modules/web/admin';
import AlbumFields, { emptyFields } from './AlbumFields';
import { Button } from './ui';

const NewAlbum = ({ onCreated, onCancel, onError }) => {
	const [fields, setFields] = useState(emptyFields);
	const [busy, setBusy] = useState(false);

	const submit = async (e) => {
		e.preventDefault();
		setBusy(true);

		const { date, ...rest } = fields;

		try {
			// An empty date lets the server use today's date.
			const album = await createAlbum(
				date.trim() ? { ...rest, date } : rest
			);
			onCreated(album.id);
		} catch (err) {
			onError(err);
			setBusy(false);
		}
	};

	return (
		<form
			onSubmit={submit}
			className='flex flex-col gap-4 border border-borderContent p-4'>
			<span className='text-lg'>New album</span>
			<AlbumFields fields={fields} onChange={setFields} />
			<div className='flex flex-row gap-2'>
				<Button type='submit' disabled={busy || !fields.name.trim()}>
					{busy ? 'Creating…' : 'Create album'}
				</Button>
				<Button variant='outline' onClick={onCancel}>
					Cancel
				</Button>
			</div>
		</form>
	);
};

export default ({ albums, onOpen, onCreated, onError }) => {
	const [creating, setCreating] = useState(false);

	const ids = Object.keys(albums).sort((a, b) => {
		const pinned = Number(!!albums[b].pinned) - Number(!!albums[a].pinned);

		return pinned || albums[a].name.localeCompare(albums[b].name);
	});

	return (
		<div className='flex flex-col gap-5'>
			{creating ? (
				<NewAlbum
					onCreated={onCreated}
					onCancel={() => setCreating(false)}
					onError={onError}
				/>
			) : (
				<div>
					<Button onClick={() => setCreating(true)}>+ New album</Button>
				</div>
			)}
			{ids.length === 0 && <span>No albums yet.</span>}
			<div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
				{ids.map((id) => {
					const album = albums[id];
					const cover =
						album.coverImage || sortedPhotoKeys(album)[0];
					const count = Object.keys(album.photos || {}).length;

					return (
						<button
							type='button'
							key={id}
							onClick={() => onOpen(id)}
							className='flex flex-col gap-2 text-left group'>
							<div className='w-full aspect-[4/3] bg-[rgba(127,127,127,0.2)] overflow-hidden'>
								{cover && (
									<img
										src={photoSrc(
											id,
											'lowres',
											cover,
											album.photos[cover]
										)}
										alt={album.name}
										loading='lazy'
										className='w-full h-full object-cover transition-all duration-300 group-hover:brightness-75'
									/>
								)}
							</div>
							<span className='text-sm'>
								{album.pinned && '📌 '}
								{album.name}
							</span>
							<span className='text-xs opacity-60'>
								{album.date} · {count} photo
								{count === 1 ? '' : 's'}
								{count === 0 && ' · hidden'}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
};
